import type {
  AmpAgentContext,
  EmailsContext,
} from "@hightouch/lib/agent-harness/shared/context-schema";

import type { InitialComposerContext } from "src/components/llm/embedded-chat/embedded-agent-chat";
import type { InteractionContextInput } from "src/graphql";
import {
  creativeBoostFromContext,
  creativeBoostToContext,
} from "src/pages/ad-studio/components/ad-studio-creative-boost";
import {
  type StyleSelection,
  styleSelectionFromContext,
  styleSelectionToContext,
} from "src/pages/ad-studio/components/ad-studio-style-select";
import { templateSelectionToContext } from "src/pages/ad-studio/components/ad-studio-template-selection";
import type { ComposerSelections } from "src/pages/projects/amp-composer/composer-selections";
import {
  type ComposerChannels,
  type SelectedOptions,
  styleSelectionOf,
} from "src/pages/projects/amp-composer/use-composer-options";

/** A picked skill as the next message's focused content names it; the backend adds the path of its staged file. */
export type SelectedSkill = { id: string; name: string };

/** One message's focused content: the focus plus the skills picked for it, which the AMP prompt has the agent read. */
export function ampFocusedContent(
  focus: object | null,
  selectedSkills: SelectedSkill[],
): string {
  const content = {
    ...focus,
    ...(selectedSkills.length > 0 ? { selectedSkills } : {}),
  };
  return Object.keys(content).length > 0 ? JSON.stringify(content) : "";
}

/** The picks a chat's history carries: the ones that hold across messages. */
export type RestoredSelections = Pick<
  ComposerSelections,
  "adsStyle" | "emailStyle" | "creativeBoost" | "useVideoEngine"
>;

/** The picks that hold across messages, as a reopened chat's last message left them. */
export function selectionsFromInitialContext(
  initial: InitialComposerContext,
): RestoredSelections {
  if (initial.harness === "v2") {
    const { ads, emails } = initial.context;
    return {
      adsStyle:
        ads?.style?.mode === "style"
          ? { kind: "style", id: ads.style.id }
          : { kind: ads?.style?.mode === "none" ? "none" : "auto" },
      emailStyle:
        emails?.style === "none"
          ? { kind: "none" }
          : emails?.style
            ? { kind: "style", id: emails.style.id }
            : { kind: "auto" },
      creativeBoost: ads?.creativeBoost ?? false,
      useVideoEngine: ads?.useVideoEngine ?? false,
    };
  }
  // Resume-only turns may omit selections; match the latest turn that carried them.
  const context = initial.interactionContexts.findLast(
    (context) =>
      context &&
      ("adStudioStyleId" in context || "adStudioStyleMode" in context),
  );
  return {
    adsStyle: styleSelectionFromContext(context),
    emailStyle: context?.emailStyleId
      ? { kind: "style", id: context.emailStyleId }
      : { kind: "auto" },
    creativeBoost: creativeBoostFromContext(context),
    useVideoEngine: context?.useVideoEngine ?? false,
  };
}

/** The selected style as `{ [key]: { id, name } }`, `"none"` for No style, or nothing for Auto or a style not in the list. */
export function selectedStyle(
  key: "adStyle" | "emailStyle",
  selection: StyleSelection,
  options: ReadonlyArray<{ id: string; name: string }> | undefined,
): Partial<Record<"adStyle" | "emailStyle", EmailsContext["style"]>> {
  if (selection.kind === "none") return { [key]: "none" };
  const style =
    selection.kind === "style"
      ? options?.find(({ id }) => id === selection.id)
      : undefined;
  return style ? { [key]: { id: style.id, name: style.name } } : {};
}

/** What the person can send: their channels, and the toggles their flags allow. */
export type ComposerSendSettings = {
  channels: ComposerChannels;
  isCreativeBoostEnabled: boolean;
  isVideoEngineEnabled: boolean;
};

/**
 * The v2 context for the next message; names accompany ids so the agent knows
 * what the ads and emails it starts are made with.
 */
export function toAmpAgentContext(
  selected: SelectedOptions,
  {
    channels,
    isCreativeBoostEnabled,
    isVideoEngineEnabled,
  }: ComposerSendSettings,
): Pick<AmpAgentContext, "ads" | "emails"> & {
  selectedSkills: SelectedSkill[];
} {
  const pickIdAndName = ({ id, name }: { id: string; name: string }) => ({
    id,
    name,
  });
  return {
    ...(channels.ad
      ? {
          ads: {
            style:
              selected.adsStyle.kind === "style"
                ? {
                    mode: "style" as const,
                    ...pickIdAndName(selected.adsStyle.style),
                  }
                : { mode: selected.adsStyle.kind },
            ...(selectedAdTemplates(selected).length
              ? { templates: selectedAdTemplates(selected).map(pickIdAndName) }
              : {}),
            // Forced off with its flag, so a chat that had it on stops sending it.
            creativeBoost: isCreativeBoostEnabled && selected.creativeBoost,
            useVideoEngine: isVideoEngineEnabled && selected.useVideoEngine,
          },
        }
      : {}),
    ...(channels.email
      ? {
          emails: {
            style:
              selected.emailStyle.kind === "style"
                ? pickIdAndName(selected.emailStyle.style)
                : selected.emailStyle.kind === "none"
                  ? ("none" as const)
                  : undefined,
            ...(selected.emailTemplates.length
              ? { templates: selected.emailTemplates.map(pickIdAndName) }
              : {}),
            ...(selected.emailComponents.length
              ? {
                  components: selected.emailComponents.map(
                    ({ id, name, componentType }) => ({
                      id,
                      name,
                      componentType,
                    }),
                  ),
                }
              : {}),
          },
        }
      : {}),
    selectedSkills: selected.skills.map(pickIdAndName),
  };
}

/**
 * The v1 context for the next message, carried to the specialists the turn
 * starts and read back to restore the chat's styles and creative boost. The
 * email templates and components go through the focused content; the agent
 * gives each email it starts one template and the components in its instructions.
 */
export function toInteractionContext(
  selected: SelectedOptions,
  {
    channels,
    isCreativeBoostEnabled,
    isVideoEngineEnabled,
  }: ComposerSendSettings,
): InteractionContextInput {
  return {
    ...(channels.ad
      ? {
          ...styleSelectionToContext(styleSelectionOf(selected.adsStyle)),
          ...templateSelectionToContext({
            ids: selectedAdTemplates(selected).map(({ id }) => id),
          }),
          ...creativeBoostToContext(
            isCreativeBoostEnabled && selected.creativeBoost,
          ),
          ...(isVideoEngineEnabled && selected.useVideoEngine
            ? { useVideoEngine: true }
            : {}),
        }
      : {}),
    ...(channels.email && selected.emailStyle.kind === "style"
      ? { emailStyleId: selected.emailStyle.style.id }
      : {}),
  };
}

export function selectedAdTemplates(selected: SelectedOptions) {
  return [
    ...new Map(
      [
        ...selected.adTemplates,
        ...selected.adTemplateFolders.flatMap((folder) => folder.templates),
      ].map((template) => [template.id, template]),
    ).values(),
  ];
}
